// utilities/stratusDocumentSelection.js
//
// SOUND-830 — which registration documents go to Kurv with a transmit. The
// dry run lists every ELIGIBLE document with `fileId`, `uploadedAt` and the
// server's `defaultSelected` (newest per slot; the current batch for
// statements/IDs). The operator ticks/unticks; the live transmit sends the
// ticked `fileIds`. Pure, so it is tested — there is no component harness
// (SOUND-679).
//
// The choice is a map fileId -> boolean. It SURVIVES re-runs of the dry run
// (picking an MCC or template re-runs it): ids still listed keep the operator's
// choice, new ids take the server default, vanished ids are dropped.

/** Can this preview be picked from? An older API sends no fileId. */
export function isSelectable(documents) {
  return Array.isArray(documents) && documents.length > 0 && documents.every((d) => typeof d?.fileId === "string" && d.fileId);
}

/** Initial / reconciled selection for a (new) preview. */
export function reconcileSelection(documents, previous = {}) {
  const next = {};
  if (!isSelectable(documents)) return next;
  for (const d of documents) {
    next[d.fileId] = Object.prototype.hasOwnProperty.call(previous || {}, d.fileId)
      ? Boolean(previous[d.fileId])
      : d.defaultSelected !== false;
  }
  return next;
}

export function toggleSelection(selection, fileId, checked) {
  return { ...(selection || {}), [fileId]: Boolean(checked) };
}

/** `{ selected, total }` over the listed documents. */
export function selectionCount(documents, selection) {
  const docs = Array.isArray(documents) ? documents : [];
  if (!isSelectable(docs)) return { selected: docs.length, total: docs.length };
  return { selected: docs.filter((d) => selection?.[d.fileId]).length, total: docs.length };
}

/**
 * What the live transmit sends as `fileIds`, in preview order. `undefined`
 * (omit the key) when the preview cannot be picked from — the server then
 * sends every eligible document, the pre-SOUND-830 behaviour. An empty array
 * is a real answer: send no documents.
 */
export function selectedFileIds(documents, selection) {
  if (!isSelectable(documents)) return undefined;
  return documents.filter((d) => selection?.[d.fileId]).map((d) => d.fileId);
}

/** "Uploaded 1 Sep 2026", or null when the upload date is unknown. */
export function uploadedLabel(uploadedAt) {
  if (!uploadedAt) return null;
  const d = new Date(uploadedAt);
  if (Number.isNaN(d.getTime())) return null;
  return `Uploaded ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}`;
}
